import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  MaintenanceItem,
  MaintenanceSchedule,
  getMaintenance,
  maintenanceErrMsg,
} from "../../../../Services/maintenanceApi";
import { ContractDetail, TRADE_LABELS, listContracts } from "../../../../Services/contractApi";
import { dayKey, toCalendarDay, todayDay } from "../../../../Utils/calendarDate";

/**
 * A warranty about to run out, read off the home's records (the contract
 * extractor's `warrantyExpiration`). Shown alongside maintenance as a
 * reminder — it can't be "done", only looked at. This is what the old "Top
 * actions for your home" widget showed.
 */
export interface WarrantyReminder {
  id: string; // contract id
  name: string;
  date: string; // ISO
  label: string; // trade, or "Record"
}

/** A human name for the record behind a warranty. */
const recordName = (c: ContractDetail): string => {
  const f = c.extractedFields || {};
  return (
    (f.vendorName || "").trim() ||
    (f.projectType || "").trim() ||
    (f.scopeOfWork || "").trim() ||
    c.originalFilename ||
    "Record"
  );
};

export const warrantyRemindersFrom = (contracts: ContractDetail[]): WarrantyReminder[] => {
  const today = dayKey(todayDay());
  return contracts
    .map((c) => {
      const raw = (c.extractedFields?.warrantyExpiration || "").trim();
      const day = toCalendarDay(raw);
      if (!day || dayKey(day) <= today) return null;
      return {
        id: c._id,
        name: recordName(c),
        date: new Date(Date.UTC(day.y, day.m, day.d, 12)).toISOString(),
        label: c.tradeType ? TRADE_LABELS[c.tradeType] || c.tradeType : "Record",
      };
    })
    .filter(Boolean)
    .sort((a, b) => String(a!.date).localeCompare(String(b!.date))) as WarrantyReminder[];
};

/**
 * The owner's maintenance schedule, loaded once per page and shared by the
 * Home Status "Upcoming Maintenance" card, the Maintenance tab and the
 * sidebar's overdue badge — so marking something done in one place updates
 * the others without a refetch.
 */
interface MaintenanceState {
  schedule: MaintenanceSchedule | null;
  /** Upcoming warranty expirations from the home's records. */
  warranties: WarrantyReminder[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Replace/insert an item locally after a successful write. */
  upsertItem: (item: MaintenanceItem) => void;
  removeItem: (id: string) => void;
  setSchedule: React.Dispatch<React.SetStateAction<MaintenanceSchedule | null>>;
}

const MaintenanceContext = createContext<MaintenanceState | null>(null);

export const MaintenanceProvider: React.FC<{
  propertyId: string;
  enabled: boolean;
  children: React.ReactNode;
}> = ({ propertyId, enabled, children }) => {
  const [schedule, setSchedule] = useState<MaintenanceSchedule | null>(null);
  const [warranties, setWarranties] = useState<WarrantyReminder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !propertyId) {
      setWarranties([]);
      return;
    }
    listContracts(propertyId)
      .then((rows) => {
        if (alive.current) setWarranties(warrantyRemindersFrom(rows));
      })
      .catch(() => {
        /* silent — warranties are a bonus on top of the schedule */
      });
  }, [propertyId, enabled, nonce]);

  useEffect(() => {
    if (!enabled || !propertyId) {
      setSchedule(null);
      return;
    }
    setLoading(true);
    setError(null);
    getMaintenance(propertyId)
      .then((s) => {
        if (alive.current) setSchedule(s);
      })
      .catch((e) => {
        if (alive.current) setError(maintenanceErrMsg(e, "Couldn't load your maintenance schedule."));
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
  }, [propertyId, enabled, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const upsertItem = useCallback((item: MaintenanceItem) => {
    setSchedule((prev) => {
      if (!prev) return prev;
      const i = prev.items.findIndex((x) => x._id === item._id);
      const items = i === -1 ? [...prev.items, item] : prev.items.map((x) => (x._id === item._id ? item : x));
      const recommendations = item.recommendedKey
        ? prev.recommendations.filter((r) => r.key !== item.recommendedKey)
        : prev.recommendations;
      return { ...prev, items, recommendations };
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setSchedule((prev) => (prev ? { ...prev, items: prev.items.filter((x) => x._id !== id) } : prev));
  }, []);

  return (
    <MaintenanceContext.Provider
      value={{ schedule, warranties, loading, error, reload, upsertItem, removeItem, setSchedule }}
    >
      {children}
    </MaintenanceContext.Provider>
  );
};

export const useMaintenance = (): MaintenanceState => {
  const ctx = useContext(MaintenanceContext);
  if (!ctx) throw new Error("useMaintenance must be used inside MaintenanceProvider");
  return ctx;
};
